import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { IRole } from '@core/interfaces/role.interface';
import { PermissionService } from '@core/services/permission.service';
import { EPermissions } from '@enums/permissions.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent, IConfirmDialogData } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { getRoleIcon } from '@utilities/role-icon.util';
import { translateRoleName } from '@utilities/role-name.util';
import { roleExceedsPermissions } from '@utilities/role-permission-comparison.util';
import { filter } from 'rxjs';
import { ISelectRoleDialogData, TSelectRoleDialogCloseResult } from './select-role-dialog.interface';

/**
 * Lists every role from `GET /roles` (built-ins plus this group's custom roles) with a 3-icon
 * scheme (campaign, campaign_sub, and one shared icon for every custom role) and lets the user
 * pick one. Not closable via backdrop/ESC (`disableClose: true` set by SelectRoleDialogService);
 * the explicit close (X) / Cancel controls show a discard-confirmation if the selection changed,
 * mirroring how `campaignFormLeaveGuard` guards leaving a changed campaign form section.
 *
 * Save itself is owned by this component, via `data.onSave`: clicking Save with an unchanged
 * selection just closes immediately, but an actual change shows a spinner and disables every
 * other control until `onSave`'s request settles - closing on success, or staying open on
 * failure so the user can retry (the global ApiInterceptor already shows its own error snackbar
 * for the failed request, so this doesn't show a second one on top of it).
 *
 * Always open this via SelectRoleDialogService, never directly - the service also owns the
 * "Add custom role" round-trip (see that service's doc comment).
 */
@Component({
  imports: [
    DotsSpinnerComponent,
    HasPermissionDirective,
    IconBadgeComponent,
    IconComponent,
    MatButtonModule,
    MatTooltipModule,
    SideDialogComponent,
    TranslateModule,
  ],
  selector: 'app-select-role-dialog',
  templateUrl: './select-role-dialog.component.html',
})
export class SelectRoleDialogComponent {
  public readonly EPermissions = EPermissions;

  public readonly data = inject<ISelectRoleDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<SelectRoleDialogComponent, TSelectRoleDialogCloseResult>);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);

  /** The currently logged-in user's own permissions - not the role being assigned. Reactive so a
   * permission change elsewhere in the session is reflected immediately - see
   * AddCustomRoleDialogComponent's identical field for the same reasoning. */
  private readonly currentUserPermissions = toSignal(this.permissionService.currentUserPermissions$, {
    initialValue: this.permissionService.currentUserPermissions,
  });

  /** Shown as a tooltip on any role option the current user can't assign - see isRoleAssignable. */
  public readonly cannotAssignRoleMessage = this.translate.instant(
    'pages.settings.userManagement.selectRoleDialog.cannotAssignRole',
  ) as string;

  public readonly selectedRoleId = signal<string | null>(this.data.currentRoleId);
  public readonly hasChanged = computed(() => this.selectedRoleId() !== this.data.savedRoleId);
  public readonly submitLoading = signal<boolean>(false);

  public getRoleName(role: IRole): string {
    return translateRoleName(role, this.translate);
  }

  public getRoleIcon(role: IRole): string {
    return getRoleIcon(role);
  }

  /** A role can only be assigned (to yourself or anyone else) by someone who already holds every
   * permission it grants - otherwise this dialog could hand out broader access than the acting
   * user has themselves. True only when `role` doesn't exceed the current user's own permissions. */
  public isRoleAssignable(role: IRole): boolean {
    return !roleExceedsPermissions(role, this.currentUserPermissions());
  }

  public onRoleClick(role: IRole): void {
    if (this.submitLoading() || !this.isRoleAssignable(role)) {
      return;
    }

    this.selectedRoleId.set(role.id);
  }

  public onAddCustomRoleClick(): void {
    if (this.submitLoading()) {
      return;
    }

    this.dialogRef.close({ selectedRoleId: this.selectedRoleId(), type: 'addCustomRole' });
  }

  public onSaveClick(): void {
    const roleId = this.selectedRoleId();

    if (!roleId || this.submitLoading()) {
      return;
    }

    if (roleId === this.data.savedRoleId) {
      this.dialogRef.close({ roleId, type: 'save' });

      return;
    }

    // Save-time guard, same reasoning as CampaignSubUserInvitationComponent.onSubmit - defends
    // against the selected role becoming unassignable between selection and submit (e.g. the
    // current user's own permissions shrinking while this dialog is still open). Scoped to an
    // actual change only - re-saving the already-persisted role above always just closes, since
    // nothing is actually being granted or requested there.
    const selectedRole = this.data.roles.find((role) => role.id === roleId);

    if (!selectedRole || !this.isRoleAssignable(selectedRole)) {
      return;
    }

    this.submitLoading.set(true);

    this.data.onSave(roleId).subscribe({
      error: () => {
        this.submitLoading.set(false);
      },
      next: () => {
        this.submitLoading.set(false);
        this.dialogRef.close({ roleId, type: 'save' });
      },
    });
  }

  public onCloseClick(): void {
    if (this.submitLoading()) {
      return;
    }

    if (!this.hasChanged()) {
      this.dialogRef.close(undefined);

      return;
    }

    const confirmRef = this.dialog.open<ConfirmDialogComponent, IConfirmDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('pages.settings.userManagement.discardChangesDialog.cancel'),
        confirmText: this.translate.instant('pages.settings.userManagement.discardChangesDialog.confirm'),
        message: this.translate.instant('pages.settings.userManagement.discardChangesDialog.message'),
        swapButtons: true,
        title: this.translate.instant('pages.settings.userManagement.discardChangesDialog.title'),
      } as IConfirmDialogData,
      width: '400px',
    });

    confirmRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => this.dialogRef.close(undefined));
  }
}
